import React from 'react'
import { useTranslation } from 'react-i18next'
import { useField } from 'formik'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'

import { FileUploader, UploadButton } from '@carsys/ui'

import { useUploadImage } from './use-upload-image'
import { MediaCategory } from './media-category'

interface ImageUploaderProps {
  name: string
  category: MediaCategory
}

export const ImageUploader = ({ name, category }: ImageUploaderProps) => {
  const { t } = useTranslation()
  const [image, setImage] = React.useState<string>('')
  const [input, , helpers] = useField(name)
  const uploadImage = useUploadImage(category)
  const previewImage = image || input.value

  const handleFileSelection = (files: File[]) => {
    if (files.length > 0) {
      setImage(URL.createObjectURL(files[0]))
    } else {
      setImage('')
    }
  }

  const handleFileUpload = async (files: File[]) => {
    const { Data } = await uploadImage.mutateAsync(files[0])
    helpers.setValue(Data.URL)
  }

  React.useEffect(() => {
    return () => {
      if (image) {
        URL.revokeObjectURL(image)
      }
    }
  }, [image])

  return (
    <Grid container direction='row'>
      <Grid item xs={12}>
        <Typography variant='h4'>{t('wholesale.file_upload_title')}</Typography>
      </Grid>
      <Grid item xs={12} lg={6}>
        <Box sx={{ marginX: -2 }}>
          <FileUploader PaperComponent={Box} multiple={false} onChange={handleFileSelection}>
            {({ files, setFiles }) => (
              <Grid item xs={12} lg={6}>
                <UploadButton
                  loading={uploadImage.isLoading}
                  disabled={files.length < 1}
                  onUpload={() => {
                    handleFileUpload(files)
                    setFiles([])
                  }}
                />
              </Grid>
            )}
          </FileUploader>
        </Box>
      </Grid>
      <Grid item xs={12} md={6}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', pl: 2, pr: 2 }}>
          {previewImage && (
            <img
              src={previewImage}
              style={{ objectFit: 'contain', display: 'block', maxWidth: '100%', maxHeight: 150 }}
              data-testid='logo-preview'
              alt='logo-preview'
            />
          )}
        </Box>
      </Grid>
    </Grid>
  )
}

export default ImageUploader
